import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { userAPI } from '../../services/api';
import { User, Portfolio } from '../../types';
import { updateUser, incrementPortfolioCount, decrementPortfolioCount } from './authSlice';
import { togglePortfolioLike, addPortfolioComment, deleteComment } from './portfolioSlice';
import toast from 'react-hot-toast';

interface UserState {
  profileUser: User | null;
  portfolio: Portfolio[];
  profileCompletion: {
    completion_score: number;
    isComplete: boolean;
    missingFields: string[];
    recommendations: string[];
  } | null;
  loading: boolean;
  profileLoading: boolean;
  uploadLoading: boolean;
  error: string | null;
}

const initialState: UserState = {
  profileUser: null,
  portfolio: [],
  profileCompletion: null,
  loading: false,
  profileLoading: false,
  uploadLoading: false,
  error: null,
};

// Async thunks
export const fetchUserProfile = createAsyncThunk(
    'user/fetchUserProfile',
    async (userId: string, { rejectWithValue }) => {
      try {
        const response = await userAPI.getProfile(userId);
        return response.data.data;
      } catch (error: any) {
        console.error('Profile API error:', error); // Debug log
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch user profile');
      }
    }
  );

  export const addPortfolioItem = createAsyncThunk(
    'user/addPortfolioItem',
    async (payload: { formData: FormData, onUploadProgress?: (progressEvent: any) => void }, { dispatch, rejectWithValue }) => {
      try {
        const response = await userAPI.addPortfolioItem(payload.formData, payload.onUploadProgress);
        
        // Sync with auth state
        dispatch(incrementPortfolioCount());
        
        // Attach reward_unlocked if present in the response
        const result = response.data.data;
        result.__reward_unlocked = response.data.reward_unlocked || null;
        
        // Return the portfolio item data (with reward info attached)
        return result;
      } catch (error: any) {
        console.error('❌ Add portfolio error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to add portfolio item');
      }
    }
  );
  
  export const deletePortfolioItem = createAsyncThunk(
    'user/deletePortfolioItem',
    async (portfolioId: string, { dispatch, rejectWithValue }) => {
      try {
        // console.log('📡 API: Deleting portfolio item:', portfolioId);
        await userAPI.deletePortfolioItem(portfolioId);
        
        // Sync with auth state
        dispatch(decrementPortfolioCount());
        
        return portfolioId;
      } catch (error: any) {
        console.error('❌ Delete portfolio error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to delete portfolio item');
      }
    }
  );


export const fetchUserCollaborations = createAsyncThunk(
    'user/fetchUserCollaborations',
    async (userId: string, { rejectWithValue }) => {
      try {
        const response = await userAPI.getUserCollaborations(userId);
        return response.data.data.collaborations;
      } catch (error: any) {
        return rejectWithValue(error.response?.data?.error || 'Failed to fetch collaborations');
      }
    }
  );

export const fetchProfileComplete = createAsyncThunk(
  'user/fetchProfileComplete',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.getProfileComplete();
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch profile completion');
    }
  }
);

export const updateProfile = createAsyncThunk(
  'user/updateProfile',
  async (profileData: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await userAPI.updateProfile(profileData);
      const updatedUser = response.data.data.user;
      
      // Update auth state with new user data
      dispatch(updateUser(updatedUser));
      
      return updatedUser;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update profile');
    }
  }
);

export const updateProfileComplete = createAsyncThunk(
  'user/updateProfileComplete',
  async (profileData: any, { dispatch, rejectWithValue }) => {
    try {
      const response = await userAPI.updateProfileComplete(profileData);
      const updatedUser = response.data.data.user;
      
      // Update auth state with new user data
      dispatch(updateUser(updatedUser));
      
      return {
        user: updatedUser,
        completion_score: response.data.data.completion_score,
        isComplete: response.data.data.is_complete,
      };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to update profile');
    }
  }
);

export const uploadProfilePhoto = createAsyncThunk(
    'user/uploadProfilePhoto',
    async ({ type, file, onUploadProgress }: { type: 'profile' | 'cover'; file: FormData, onUploadProgress?: (progressEvent: any) => void }, { getState, dispatch, rejectWithValue }) => {
      try {
        // console.log('📸 Uploading photo type:', type);
        
        const response = await userAPI.uploadPhoto(type, file, onUploadProgress);
        const photoUrl = response.data.data.signed_url;
        
        // console.log('✅ Photo uploaded successfully:', photoUrl);
        
        // Get current user state to avoid overwriting other fields
        const state = getState() as any;
        const currentUser = state.auth.user;
        
        if (!currentUser) {
          throw new Error('User not found in state');
        }
        
        // Only update the specific photo field
        const isProfile = type === 'profile';
        const updateData = {
          [isProfile ? 'profile_pic' : 'cover_photo']: photoUrl,
          [isProfile ? 'profile_pic_signed' : 'cover_photo_signed']: photoUrl,
          [isProfile ? 'profile_pic_thumb_signed' : 'cover_photo_thumb_signed']: '',
          [isProfile ? 'profile_pic_medium_signed' : 'cover_photo_medium_signed']: ''
        };
        
        // console.log('🔄 Updating user with:', updateData);
        
        // Update auth state with only the photo field
        dispatch(updateUser(updateData));
        
        return {
          type,
          url: photoUrl,
        };
      } catch (error: any) {
        console.error('❌ Photo upload error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to upload photo');
      }
    }
  );
  

// export const addPortfolioItem = createAsyncThunk(
//   'user/addPortfolioItem',
//   async (portfolioData: {
//     title: string;
//     description?: string;
//     imageUrl: string;
//     tags: string[];
//   }, { rejectWithValue }) => {
//     try {
//       const response = await userAPI.addPortfolioItem(portfolioData);
//       return response.data.data.item;
//     } catch (error: any) {
//       return rejectWithValue(error.response?.data?.error || 'Failed to add portfolio item');
//     }
//   }
// );

export const removePortfolioItem = createAsyncThunk(
  'user/removePortfolioItem',
  async (itemId: string, { rejectWithValue }) => {
    try {
      await userAPI.removePortfolioItem(itemId);
      return itemId;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to remove portfolio item');
    }
  }
);

export const updatePortfolioItem = createAsyncThunk(
    'user/updatePortfolioItem',
    async ({ id, data }: { id: string; data: FormData }, { rejectWithValue }) => {
      try {
        // console.log('📡 API: Updating portfolio item:', id);
        const response = await userAPI.updatePortfolioItem(id, data);
        return response.data.data;
      } catch (error: any) {
        console.error('❌ Update portfolio error:', error);
        return rejectWithValue(error.response?.data?.error || 'Failed to update portfolio item');
      }
    }
  );

export const fetchUserPortfolio = createAsyncThunk(
  'user/fetchUserPortfolio',
  async (userId: string, { rejectWithValue }) => {
    try {
      const response = await userAPI.getUserPortfolio(userId);
      return response.data.data.portfolio;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch portfolio');
    }
  }
);

export const toggleAvailability = createAsyncThunk(
  'user/toggleAvailability',
  async (userId: string, { getState, dispatch, rejectWithValue }) => {
    try {
        const state = getState() as any;
        const currentUser = state.auth.user;
        
        const response = await userAPI.toggleAvailability(userId);
        const newAvailability = response.data.data.is_available;
        
        // // console.log('✅ Toggle availability response:', newAvailability);
        
        // Update the auth state with the new availability
        dispatch(updateUser({
          ...currentUser,
          is_available: newAvailability
        }));
        
        return newAvailability;
    } catch (error: any) {
      // console.log("response error", error)
      return rejectWithValue(error.response?.data?.error || 'Failed to toggle availability');
    }
  }
);

export const deleteAccount = createAsyncThunk(
  'user/deleteAccount',
  async (_, { rejectWithValue }) => {
    try {
      const response = await userAPI.deleteAccount();
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Failed to delete account');
    }
  }
);

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    clearProfileUser: (state) => {
      state.profileUser = null;
      state.portfolio = [];
    },
    clearError: (state) => {
      state.error = null;
    },
    updateProfileUserInState: (state, action: PayloadAction<Partial<User>>) => {
      if (state.profileUser) {
        state.profileUser = { ...state.profileUser, ...action.payload };
      }
    },
    addPortfolioToState: (state, action: PayloadAction<Portfolio>) => {
      state.portfolio.unshift(action.payload);
    },
    removePortfolioFromState: (state, action: PayloadAction<string>) => {
      state.portfolio = state.portfolio.filter(item => item.id !== action.payload);
    },
    removeBatchPortfolioFromState: (state, action: PayloadAction<string[]>) => {
      const idsToRemove = new Set(action.payload);
      state.portfolio = state.portfolio.filter(item => !idsToRemove.has(item.id));
    },
    updatePortfolioInState: (state, action: PayloadAction<Portfolio>) => {
      const index = state.portfolio.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        state.portfolio[index] = action.payload;
      }
    },

    updatePortfolioCommentsCount: (state, action: PayloadAction<{ id: string, positive: boolean}>) => {
      const index = state.portfolio.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        if(action.payload.positive) {
          // console.log("Heleloo")
          state.portfolio[index].comments_count += 1
        } else {
          state.portfolio[index].comments_count -= 1
        }
      }
    },

    updatePortfolioLikesCount: (state, action: PayloadAction<{ id: string, positive: boolean}>) => {
      const index = state.portfolio.findIndex(item => item.id === action.payload.id);
      if (index !== -1) {
        if(action.payload.positive) {
          state.portfolio[index].likes += 1
        } else {
          state.portfolio[index].likes -= 1
        }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch user profile
      .addCase(fetchUserProfile.pending, (state) => {
        state.profileLoading = true;
        state.error = null;
        state.profileUser = null;
        state.portfolio = [];
      })
      .addCase(fetchUserProfile.fulfilled, (state, action) => {
        state.profileLoading = false;
        state.profileUser = action.payload.user; // Backend now includes portfolio_items_count in user object
        state.portfolio = action.payload.portfolio || [];
      })
      .addCase(fetchUserProfile.rejected, (state, action) => {
        state.profileLoading = false;
        state.error = action.payload as string;
      })
      
      // Fetch profile completion
      .addCase(fetchProfileComplete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProfileComplete.fulfilled, (state, action) => {
        state.loading = false;
        state.profileCompletion = {
          completion_score: action.payload.completion_score,
          isComplete: action.payload.is_complete,
          missingFields: action.payload.missing_fields || [],
          recommendations: action.payload.recommendations || [],
        };
        // The portfolio_items_count is now synced via the user object in auth state or profileUser
      })
      .addCase(fetchProfileComplete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to load profile completion');
      })
      
      // Update profile
      .addCase(updateProfile.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfile.fulfilled, (state, action) => {
        state.loading = false;
        if (state.profileUser) {
          state.profileUser = action.payload;
        }
        toast.success('Profile updated successfully!');
      })
      .addCase(updateProfile.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to update profile');
      })
      
      // Update profile complete
      .addCase(updateProfileComplete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateProfileComplete.fulfilled, (state, action) => {
        state.loading = false;
        if (state.profileUser) {
          state.profileUser = action.payload.user;
        }
        state.profileCompletion = {
          completion_score: action.payload.completion_score,
          isComplete: action.payload.isComplete,
          missingFields: [],
          recommendations: [],
        };
        toast.success('Profile updated successfully!');
      })
      .addCase(updateProfileComplete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error('Failed to update profile');
      })
      
      // Upload profile photo
      .addCase(uploadProfilePhoto.pending, (state) => {
        state.uploadLoading = true;
        state.error = null;
      })
      .addCase(uploadProfilePhoto.fulfilled, (state, action) => {
        state.uploadLoading = false;
        const { type, url } = action.payload;
        
        if (state.profileUser) {
          if (type === 'profile') {
            state.profileUser.profile_pic = url;
          } else {
            state.profileUser.cover_photo = url;
          }
        }
        
        toast.success(`${type === 'profile' ? 'Profile' : 'Cover'} photo updated!`);
      })
      .addCase(uploadProfilePhoto.rejected, (state, action) => {
        state.uploadLoading = false;
        state.error = action.payload as string;
        toast.error('Failed to upload photo');
      })
      
      // Add portfolio item
      .addCase(addPortfolioItem.pending, (state) => {
        // [Background upload] Do not set state.loading = true to keep profile interactive during upload
        state.loading = false;
        state.error = null;
      })
      .addCase(addPortfolioItem.fulfilled, (state, action) => {
        state.loading = false;
        
        // Add the new portfolio item to the beginning of the array
        state.portfolio.unshift(action.payload);
        // console.log('✅ Portfolio updated in state:', state.profileUser);

        
        if (state.profileUser) {
          state.profileUser.portfolio_items_count = (state.profileUser.portfolio_items_count || 0) + 1;
        }

        // We don't have access to dispatch here directly in extraReducers 
        // but we can handle it in the thunk if needed, or just let the component handle it.
        // Wait, extraReducers is for state updates. Thunks have access to dispatch.
      })
      .addCase(addPortfolioItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to add portfolio item');
      })
      
      // Remove portfolio item
      .addCase(removePortfolioItem.fulfilled, (state, action) => {
        state.portfolio = state.portfolio.filter(item => item.id !== action.payload);
        toast.success('Portfolio item removed');
      })
      .addCase(removePortfolioItem.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to remove portfolio item');
      })
      
      .addCase(updatePortfolioItem.fulfilled, (state, action) => {
        const index = state.portfolio.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.portfolio[index] = action.payload;
        }
        toast.success('Portfolio item updated successfully!');
      })

      .addCase(updatePortfolioItem.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to update portfolio item');
      })
      
      // Fetch user portfolio
      .addCase(fetchUserPortfolio.fulfilled, (state, action) => {
        state.portfolio = action.payload;
      })
      .addCase(fetchUserPortfolio.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to load portfolio');
      })
      
      // Toggle availability
      .addCase(toggleAvailability.fulfilled, (state, action) => {
        const newAvailability = action.payload;
        // Only update profileUser if viewing another user's profile
        if (state.profileUser) {
          state.profileUser.is_available = newAvailability;
        }
        
        // Note: Auth state is already updated in the thunk above
        // toast.success(`Availability ${newAvailability ? 'enabled' : 'disabled'}`);
      })
      
      .addCase(toggleAvailability.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error('Failed to update availability');
      })
      
      .addCase(deletePortfolioItem.fulfilled, (state, action) => {
        state.portfolio = state.portfolio.filter(item => item.id !== action.payload);
        toast.success('Portfolio item deleted successfully!');
      })
      .addCase(togglePortfolioLike.fulfilled, (state, action) => {
        const { is_liked } = action.payload;
        const id = action.meta.arg;
        const item = state.portfolio.find((p) => p.id === id);
        if (item) {
          item.hasLiked = is_liked;
          if (is_liked) {
             item.likes = (item.likes || 0) + 1;
          } else {
             item.likes = Math.max(0, (item.likes || 1) - 1);
          }
        }
      })
      .addCase(addPortfolioComment.fulfilled, (state, action) => {
        const { portfolio_id } = action.payload;
        const item = state.portfolio.find((p) => p.id === portfolio_id);
        if (item) {
          item.comments_count += 1;
        }
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        const { portfolioId } = action.payload;
        const item = state.portfolio.find((p) => p.id === portfolioId);
        if (item) {
          item.comments_count = Math.max(0, item.comments_count - 1);
        }
      })
      .addCase(deleteAccount.fulfilled, (state) => {
        state.profileUser = null;
        state.portfolio = [];
        // toast.success('Account scheduled for deletion');
      })
      .addCase(deleteAccount.rejected, (state, action) => {
        state.error = action.payload as string;
        toast.error(action.payload as string || 'Failed to delete account');
      });
  },
});

export const {
  clearProfileUser,
  clearError,
  updateProfileUserInState,
  addPortfolioToState,
  removePortfolioFromState,
  removeBatchPortfolioFromState,
  updatePortfolioInState,
  updatePortfolioCommentsCount,
  updatePortfolioLikesCount,
} = userSlice.actions;

export default userSlice.reducer;
